import Decimal from 'decimal.js'
import type { BalanceItem, LedgerItem, MoneyDto, SaleItem } from '../api/admin.api'

export type MoneySummary = {
  currency: string
  amount: string
}

export type ReportingCurrency = 'BRL' | 'USD'

export type ReportingAggregate = {
  rows: MoneySummary[]
  convertedCount: number
  unconvertedCount: number
}

function amountDecimal(value: string): Decimal {
  try {
    return new Decimal(value)
  } catch {
    return new Decimal(0)
  }
}

export function addAmount(current: string, amount: Decimal.Value) {
  return amountDecimal(current).plus(amount).toFixed(3)
}

export function isPositiveAmount(value: string) {
  return amountDecimal(value).gt(0)
}

function isNonZeroAmount(value: { amount: string }) {
  return !amountDecimal(value.amount).isZero()
}

export function balanceHasMoney(item: BalanceItem) {
  return (
    isNonZeroAmount(item.available) ||
    isNonZeroAmount(item.pending) ||
    isNonZeroAmount(item.reserve) ||
    isNonZeroAmount(item.openDebt) ||
    isNonZeroAmount(item.processingWithdrawal) ||
    isNonZeroAmount(item.processingDebtRecovery)
  )
}

export function sortCurrencies(values: Iterable<string>) {
  const priority = new Map([
    ['BRL', 0],
    ['USD', 1],
  ])
  return [...new Set(values)].sort((left, right) => {
    const leftPriority = priority.get(left) ?? 2
    const rightPriority = priority.get(right) ?? 2
    if (leftPriority !== rightPriority) return leftPriority - rightPriority
    return left.localeCompare(right)
  })
}

export function resolveSelectedCurrency(preferredCurrency: string, availableCurrencies: string[]) {
  const nextCurrency = availableCurrencies[0]
  return nextCurrency !== undefined && !availableCurrencies.includes(preferredCurrency)
    ? nextCurrency
    : preferredCurrency
}

export function ledgerAmountDecimal(entry: LedgerItem) {
  return amountDecimal(entry.amount.amount)
}

export function sumLedgerByCurrency(
  items: LedgerItem[],
  predicate: (item: LedgerItem) => boolean = () => true,
): MoneySummary[] {
  const totals = new Map<string, string>()
  for (const item of items) {
    if (!predicate(item)) continue
    totals.set(
      item.amount.currency,
      addAmount(totals.get(item.amount.currency) ?? '0.000', ledgerAmountDecimal(item)),
    )
  }
  return [...totals.entries()].map(([currency, amount]) => ({ currency, amount }))
}

export function reportingAmountOf(item: SaleItem, currency: ReportingCurrency) {
  const reportingAmount = currency === 'BRL' ? item.reportingAmountBrl : item.reportingAmountUsd
  if (reportingAmount) {
    return { amount: reportingAmount, converted: item.amountPaid.currency !== currency }
  }
  if (item.grossAmount.currency === currency) {
    return { amount: item.grossAmount, converted: item.amountPaid.currency !== currency }
  }
  if (item.amountPaid.currency === currency) {
    return { amount: item.amountPaid, converted: false }
  }
  return null
}

export function reportingAmountsOf(item: SaleItem): MoneySummary[] {
  return [
    item.reportingAmountBrl ? moneySummary(item.reportingAmountBrl) : null,
    item.reportingAmountUsd ? moneySummary(item.reportingAmountUsd) : null,
  ].filter((row): row is MoneySummary => row !== null)
}

export function aggregateSalesMoney(
  items: SaleItem[],
  predicate: (item: SaleItem) => boolean,
  currency: ReportingCurrency,
): ReportingAggregate {
  let total = new Decimal(0)
  let convertedCount = 0
  let unconvertedCount = 0
  for (const item of items) {
    if (!predicate(item)) continue
    const reportingAmount = reportingAmountOf(item, currency)
    if (!reportingAmount) {
      unconvertedCount += 1
      continue
    }
    total = total.plus(amountDecimal(reportingAmount.amount.amount))
    if (reportingAmount.converted) {
      convertedCount += 1
    }
  }
  return {
    rows: [{ currency, amount: total.toFixed(3) }],
    convertedCount,
    unconvertedCount,
  }
}

function moneySummary(amount: MoneyDto): MoneySummary {
  return { currency: amount.currency, amount: amount.amount }
}
